import { useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import Dropdown from "../../../../parts/dropdown";
import Modal from "../../../../parts/modal-new";
import SvgIcon from "../../../../parts/SvgIcon";
import getAvatarUrl from "../../../../utils/get-avatar-url";
import { isPerformActiveCoach } from "../../../../utils/user-helpers";
import {
  getRoleNameById,
  getRolesToShow,
  translateOrganizationPermission,
} from "./utils/manage-coaches-utils";

export const ManageCoachModalComponent = ({ locals }) => {
  const { t } = useTranslation(["dashboard", "common"]);
  const {
    organizationRoles = [],
    query = {},
    usersOrganization,
    organizationOwner,
    activeUser,
  } = locals;
  const { organizationActiveUsers = [] } = usersOrganization;
  const { coachId = null, roleOrganizationName = "" } = query;
  const coach = organizationActiveUsers.find(({ _id }) => _id === coachId);
  const {
    name = "",
    email = "",
    organizationPermissions = [],
    organizationRoleId = null,
    organizationUserStatus = false,
  } = coach;
  const theme = activeUser?.webAppTheme || "dark";
  const image = getAvatarUrl(coach, null, theme, theme !== "dark");
  //roles to show in dropdwon
  const rolesToShow = getRolesToShow(organizationRoles, roleOrganizationName);

  //state for the checkbox
  const [selectedPermissionsState, setSelectedPermissionsState] = useState(
    organizationPermissions
  );
  //handle the selected role
  const [selectedRoleState, setSelectedRoleState] =
    useState(organizationRoleId);
  //this state is because if the role is admin or owner, all the permissions should be enable
  const [selectedRoleNameState, setSelectedRoleNameState] = useState(
    getRoleNameById(organizationRoles, organizationRoleId)
  );

  const userIsPush = Boolean(organizationOwner.pushAccountInfo);
  const userIsPress = Boolean(organizationOwner.pressAccountInfo);
  const userIsPerform = isPerformActiveCoach(organizationOwner);

  //filtering the permissions to show if the user is push, press, or perform
  const filteredPermissions = locals.organizationPermissions.filter(
    ({ permission }) => {
      return !!(
        (permission !== "PUSH" &&
          permission !== "PRESS" &&
          permission !== "PERFORM") ||
        (permission === "PUSH" && userIsPush) ||
        (permission === "PRESS" && userIsPress) ||
        (permission === "PERFORM" && userIsPerform)
      );
    }
  );

  //to the select all permissions function
  const permissionIds = filteredPermissions.map((permission) => permission._id);

  /**
   * Capture the role selected
   * @param {*} roleId
   */
  const handleSetSelectedRole = (roleId) => {
    const roleName = getRoleNameById(organizationRoles, roleId);
    const previousRoleName = selectedRoleNameState;
    setSelectedRoleState(roleId);
    setSelectedRoleNameState(roleName);
    // Admin/Owner get every permission checked AND locked (see
    // handleSelectionChange/the cursor style below, both still keyed on the
    // literal "Admin"/"Owner" check). Team Coach starts checked too, but
    // stays editable — it isn't part of those other checks.
    const forcesEveryPermission = (name) =>
      name === "Admin" || name === "Owner" || name === "Team Coach";
    if (forcesEveryPermission(roleName)) {
      selectAll(true);
    } else if (forcesEveryPermission(previousRoleName)) {
      // Leaving a role that force-checks every permission — reset instead of
      // silently carrying permissions over that only ever came from that role
      setSelectedPermissionsState([""]);
    }
  };

  /**
   * Handle the selected permissions
   * @param {*} id
   */
  const handleSelectionChange = (id) => {
    if (
      selectedRoleNameState !== "Admin" &&
      selectedRoleNameState !== "Owner"
    ) {
      //code for the check color
      let data = JSON.parse(JSON.stringify(selectedPermissionsState));
      if (!selectedPermissionsState.includes(id)) {
        if (data.length === 1 && data[0].length === 0) {
          data = [id];
        } else {
          data.push(id);
        }
      } else {
        if (selectedPermissionsState.length > 0) {
          const filetered = data.filter((val) => val !== id);
          data = filetered.length > 0 ? filetered : [""];
        }
      }
      setSelectedPermissionsState(data);
    }
  };

  /**
   * Select all the permissions
   */
  const selectAll = (adminSelected = false) => {
    const reset =
      !adminSelected &&
      selectedRoleNameState !== "Admin" &&
      selectedRoleNameState !== "Owner";

    if (
      JSON.stringify(permissionIds) === JSON.stringify(selectedPermissionsState)
    ) {
      if (reset) {
        //unselect all
        setSelectedPermissionsState([""]);
      }
    } else {
      //select all
      setSelectedPermissionsState(permissionIds);
    }
  };

  /**
   * Save user's changes
   * @param {*} e
   */
  const updateUser = (e) => {
    if (selectedPermissionsState[0].length > 0) {
      e.stopPropagation();
      e.preventDefault();

      const body = {
        organizationRoleId: selectedRoleState,
        oldOrganizationPermissions: organizationPermissions, //the permissions before the update
        organizationPermissions: selectedPermissionsState, //the permissions after the update
        userId: coachId,
        organizationUserStatus,
      };

      // A Team Coach never holds assigned clients — a coach with a roster who's switched
      // to Team Coach has that roster moved to the Owner (see Phase 39, team-coach-migration.js).
      // A Team Coach's own athletes is always empty by that same invariant, so this alone
      // also rules out warning about a no-op re-save.
      const assignedClientsCount = coach.athletes?.length || 0;
      if (selectedRoleNameState === "Team Coach" && assignedClientsCount > 0) {
        const confirmText = t("manageCoaches.teamCoachRoleChangeConfirm", {
          name,
          count: assignedClientsCount,
          ownerName: organizationOwner.name,
        });
        if (!process.browser || !confirm(confirmText)) {
          return;
        }
      }

      submit(`/actions/organizations/update-user`, {
        method: "POST",
        body,
      });
    }
  };

  return (
    <Modal
      className={"manage-coach"}
      changeCloseIcon={true}
      overflowModal={true}
      title={t("manageCoaches.manageCoach")}
    >
      <div className="body">
        <article>
          <div className="left">
            <div
              className="avatar"
              style={{
                background: `url(${image})`,
                backgroundPosition: "center center",
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
              }}
            ></div>
            <div className="coach-info">
              <p className="s1">{name}</p>
              <p className="p4 opacity-70">{email}</p>
            </div>
          </div>
          <div className="right">
            <Dropdown
              summaryText={selectedRoleNameState}
              options={rolesToShow.map((role) => {
                return { text: role.role, value: role._id };
              })}
              onSelectFn={handleSetSelectedRole}
            />
          </div>
        </article>
        <label>{t("manageCoaches.permissions")}</label>
        <section role="table-wrapper" data-borders-inside>
          <table role="report-table">
            <thead>
              <th>
                <SvgIcon
                  onClick={() => {
                    selectAll();
                  }}
                  src={`/img/new-icons/${
                    JSON.stringify(permissionIds) ===
                    JSON.stringify(selectedPermissionsState)
                      ? "checked-square.svg"
                      : "unchecked-square.svg"
                  }`}
                  noFilterOnLight
                />
              </th>
              <th>
                <p className="s1">{t("manageCoaches.permissions")}</p>
              </th>
            </thead>
            <tbody>
              {filteredPermissions.map((item) => {
                const { _id, permission } = item;
                const permissionFormat = translateOrganizationPermission(
                  t,
                  permission
                );
                return (
                  <tr key={_id}>
                    <td>
                      {selectedPermissionsState.includes(_id) ? (
                        <SvgIcon
                          src="/img/new-icons/checked-square.svg"
                          alt="checked"
                          style={{
                            cursor:
                              selectedRoleNameState === "Admin" ||
                              selectedRoleNameState === "Owner"
                                ? "not-allowed"
                                : "pointer",
                          }}
                          onClick={() => handleSelectionChange(_id)}
                          noFilterOnLight
                        />
                      ) : (
                        <SvgIcon
                          src="/img/new-icons/unchecked-square.svg"
                          alt="unchecked"
                          onClick={() => handleSelectionChange(_id)}
                        />
                      )}
                    </td>
                    <td>
                      <span className="permission-name-manage">
                        {permissionFormat}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      </div>
      <footer>
        <button
          data-button-type="secondary"
          data-action="cancel"
          onClick={() =>
            submit("/actions/refresh", {
              method: "GET",
              body: {
                modal: "",
              },
            })
          }
          type="button"
        >
          {t("common:cancel")}
        </button>
        <button
          data-button-type="primary"
          disabled={selectedPermissionsState[0].length > 0 ? undefined : true}
          onClick={updateUser}
          type="button"
        >
          {t("common:save")}
        </button>
      </footer>
    </Modal>
  );
};
